import React, { useState, useContext, useRef, useEffect } from 'react';
import { 
  XMarkIcon,
  UserIcon,
  BuildingOfficeIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { AuthContext } from '../../../../context/AuthContext';
import { createConnectionRequest } from '../../../../services/firebase/startup-opportunities';
import { notifyAdminOfNewRequest } from '../../../../services/firebase/connection-facilitation';
import { useOpportunityAnalytics } from '../../../../hooks/useOpportunityAnalytics';
import useScrollLock from '../../../../hooks/useScrollLock';

/**
 * ConnectionRequestModal component for submitting connection requests to founders
 * Features form validation, user feedback, and next steps information
 */
const ConnectionRequestModal = ({ 
  opportunity, 
  isOpen, 
  onClose, 
  onSuccess,
  className = '',
  source = 'details_modal' // Source context for analytics
}) => {
  const { user, userProfile } = useContext(AuthContext);
  const { trackConnection, trackInteraction } = useOpportunityAnalytics();
  const [userMessage, setUserMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', null
  const [errorMessage, setErrorMessage] = useState('');
  const modalOpenTime = useRef(null);
  const typingStartTime = useRef(null);
  const typingEndTime = useRef(null);
  const typingTimer = useRef(null);
  
  // Use scroll lock hook to prevent background scrolling
  const { isLocked } = useScrollLock({ 
    isActive: isOpen,
    restoreScrollOnUnmount: true 
  });

  // Character limit for user message
  const MESSAGE_LIMIT = 1000;

  // Reset form when modal opens and track modal open time
  React.useEffect(() => {
    if (isOpen) {
      setUserMessage('');
      setSubmitStatus(null);
      setErrorMessage('');
      modalOpenTime.current = Date.now();
      
      // Track modal open
      if (opportunity?.id) {
        trackInteraction(opportunity.id, 'connection_modal_open', {
          source,
          companyName: opportunity.companyName,
          roleArea: opportunity.roleArea
        }).catch(err => console.warn('Failed to track modal open:', err));
      }
    }
  }, [isOpen, opportunity?.id, trackInteraction, source, opportunity?.companyName, opportunity?.roleArea]);
  
  // Track when modal closes
  React.useEffect(() => {
    return () => {
      if (!isOpen || !modalOpenTime.current || !opportunity?.id) return;
      
      const timeSpent = Date.now() - modalOpenTime.current;
      if (timeSpent > 1000) { // Only track if modal was open for more than 1 second
        trackInteraction(opportunity.id, 'connection_modal_close', {
          source,
          timeSpent: Math.round(timeSpent / 1000), // Convert to seconds
          status: submitStatus,
          messageLength: userMessage.length,
          companyName: opportunity.companyName
        }).catch(err => console.warn('Failed to track modal close:', err));
      }
    };
  }, [isOpen, opportunity?.id, submitStatus, userMessage.length, trackInteraction, source, opportunity?.companyName]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      
      if (e.key === 'Escape') {
        handleClose(e);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      
      // Focus management - focus the modal container when opened
      const modalContainer = document.querySelector('.modal-container');
      if (modalContainer) {
        modalContainer.focus();
      }
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  // Handle missing opportunity data or closed modal
  if (!opportunity || !isOpen) {
    return null;
  }

  const {
    id: opportunityId,
    companyName = 'Unknown Company',
    companyLogo = null,
    roleArea = '',
    founderInfo = null,
    // Company data from Firestore companies collection
    company = null
  } = opportunity;

  // Get company data - prioritize company object from Firestore, fallback to opportunity fields
  const getCompanyData = () => {
    if (company) {
      return {
        name: company.name || companyName,
        logo: company.logo || companyLogo
      };
    }
    
    return {
      name: companyName,
      logo: companyLogo
    };
  };

  // Get company logo URL or fallback
  const getCompanyLogoUrl = () => {
    const companyData = getCompanyData();
    
    // First try the Firestore logo URL
    if (companyData.logo) return companyData.logo;
    
    // Fallback to known company logos in public folder
    const logoMap = {
      'stripe': '/img/logos/stripe-logo.png',
      'airtable': '/img/logos/airtable-logo.jpeg',
      'spacex': '/img/logos/SpaceX-logo.svg',
      'aws': '/img/logos/aws-logo.png',
      'databricks': '/img/logos/databricks-logo.png',
      'openai': '/img/logos/openai-logo.jpeg',
      'deloitte': '/img/logos/deloitte-logo-.png',
      'jpmorgan': '/img/logos/jpmorgan-logo.png',
      'mckinsey': '/img/logos/mckinsey-logo.png',
      'appian': '/img/logos/appian-logo.png',
      'carlyle': '/img/logos/carlyle-logo.png'
    };
    
    const companyKey = companyData.name.toLowerCase().replace(/\s+/g, '');
    return logoMap[companyKey] || null;
  };

  // Handle form submission
  // Track typing activity
  const handleMessageChange = (e) => {
    const newValue = e.target.value;
    setUserMessage(newValue);
    
    // Start tracking typing
    if (!typingStartTime.current && newValue.length > 0) {
      typingStartTime.current = Date.now();
    }
    
    // Update typing end time
    typingEndTime.current = Date.now();
    
    // Clear previous timer
    if (typingTimer.current) {
      clearTimeout(typingTimer.current);
    }
    
    // Set timer to track typing session end
    typingTimer.current = setTimeout(() => {
      if (typingStartTime.current && typingEndTime.current && opportunity?.id) {
        const typingDuration = (typingEndTime.current - typingStartTime.current) / 1000;
        
        // Only track if typing duration is significant (> 2 seconds)
        if (typingDuration > 2) {
          trackInteraction(opportunity.id, 'message_typing', {
            duration: Math.round(typingDuration),
            messageLength: newValue.length,
            companyName: opportunity.companyName
          }).catch(err => console.warn('Failed to track typing:', err));
        }
        
        // Reset for next typing session
        typingStartTime.current = null;
      }
    }, 1500); // Consider typing session ended after 1.5s of inactivity
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate user authentication
    if (!user) {
      setSubmitStatus('error');
      setErrorMessage('You must be logged in to submit a connection request.');
      return;
    }

    // Validate message
    const trimmedMessage = userMessage.trim();
    if (!trimmedMessage) {
      setSubmitStatus('error');
      setErrorMessage('Please provide a message about your interest in this opportunity.');
      return;
    }

    if (trimmedMessage.length > MESSAGE_LIMIT) {
      setSubmitStatus('error');
      setErrorMessage(`Message must be ${MESSAGE_LIMIT} characters or less.`);
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage('');
    
    // Track submission attempt
    trackInteraction(opportunity.id, 'connection_submit_attempt', {
      messageLength: trimmedMessage.length,
      timeToSubmit: modalOpenTime.current ? 
        Math.round((Date.now() - modalOpenTime.current) / 1000) : null,
      companyName: opportunity.companyName
    }).catch(err => console.warn('Failed to track submission attempt:', err));

    try {
      // Create connection request
      const result = await createConnectionRequest({
        userId: user.uid,
        opportunityId: opportunityId,
        userMessage: trimmedMessage
      });

      if (result.success) {
        setSubmitStatus('success');
        
        // Track connection request analytics
        try {
          await trackConnection(opportunityId, result.data.id, {
            source,
            messageLength: trimmedMessage.length,
            companyName: companyName,
            roleArea: roleArea,
            founderName: founderInfo?.name || null
          });
        } catch (analyticsError) {
          console.warn('Failed to track connection request:', analyticsError);
          // Don't block on analytics failure
        }
        
        // Send admin notification (don't block on this)
        try {
          const userData = {
            name: getUserDisplayName(),
            email: user.email || userProfile?.email || 'unknown@example.com'
          };
          
          const opportunityData = {
            companyName: companyName,
            roleArea: roleArea
          };
          
          const requestData = {
            id: result.data.id,
            userMessage: trimmedMessage
          };
          
          await notifyAdminOfNewRequest(requestData, userData, opportunityData);
        } catch (notificationError) {
          console.warn('Failed to send admin notification:', notificationError);
          // Don't show error to user as the main request was successful
        }
        
        // Call success callback if provided
        if (onSuccess && typeof onSuccess === 'function') {
          onSuccess(result.data);
        }
      } else {
        setSubmitStatus('error');
        setErrorMessage(result.error || 'Failed to submit connection request. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting connection request:', error);
      setSubmitStatus('error');
      setErrorMessage('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle modal close
  const handleClose = (e) => {
    e.stopPropagation();
    if (onClose && typeof onClose === 'function') {
      onClose();
    }
  };

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose(e);
    }
  };

  // Generate company initials for fallback logo
  const getCompanyInitials = () => {
    return companyName
      .split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('');
  };

  // Get user display name
  const getUserDisplayName = () => {
    // Try basicInfo fields first (current structure)
    if (userProfile?.basicInfo?.firstName && userProfile?.basicInfo?.lastName) {
      return `${userProfile.basicInfo.firstName} ${userProfile.basicInfo.lastName}`;
    }
    if (userProfile?.basicInfo?.firstName) {
      return userProfile.basicInfo.firstName;
    }
    if (userProfile?.basicInfo?.name) {
      return userProfile.basicInfo.name;
    }
    // Fallback to displayName
    if (user?.displayName) {
      return user.displayName;
    }
    // Legacy fallback to root-level fields
    if (userProfile?.firstName && userProfile?.lastName) {
      return `${userProfile.firstName} ${userProfile.lastName}`;
    }
    if (userProfile?.firstName) {
      return userProfile.firstName;
    }
    // Final fallbacks
    if (user?.email) {
      return user.email.split('@')[0];
    }
    return 'User';
  };

  // Character count display
  const characterCount = userMessage.length;
  const isOverLimit = characterCount > MESSAGE_LIMIT;
  const isNearLimit = characterCount > MESSAGE_LIMIT * 0.8;

  return (
    <div 
      className={`modal-backdrop ${className}`}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="connection-modal-title"
      aria-describedby="connection-modal-description"
    >
      <div 
        className="modal-container max-w-2xl"
        onClick={(e) => e.stopPropagation()}
        tabIndex={-1}
      >
        {/* Header */}
        <div className="modal-header">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Company Logo */}
              <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                {getCompanyLogoUrl() ? (
                  <>
                    <img 
                      src={getCompanyLogoUrl()} 
                      alt={`${companyName} logo`}
                      className="h-10 w-10 object-contain rounded-full"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'flex';
                      }}
                    />
                    <div 
                      className="hidden w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-purple-700 
                                 flex items-center justify-center text-white font-bold text-lg"
                    >
                      {getCompanyInitials()}
                    </div>
                  </>
                ) : (
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-purple-700 
                                 flex items-center justify-center text-white font-bold text-lg">
                    {getCompanyInitials()}
                  </div>
                )}
              </div>
              
              <div>
                <h2 
                  id="connection-modal-title"
                  className="text-xl font-bold text-gray-900 dark:text-white"
                >
                  Connect with {companyName}
                </h2>
                <p className="text-gray-600 dark:text-gray-400">
                  {companyName} • {roleArea}
                </p>
              </div>
            </div>

            {/* Close Button */}
            <button
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 
                       hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              aria-label="Close modal"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="modal-scroll-container">
          <div className="modal-content" id="connection-modal-description">
          {submitStatus === 'success' ? (
            /* Success State */
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full 
                             flex items-center justify-center mx-auto mb-4">
                <CheckCircleIcon className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                Connection Request Sent!
              </h3>
              
              <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
                Your request has been submitted to our team for review. We'll facilitate an introduction 
                if it's a good match.
              </p>

              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 mb-6 text-left">
                <div className="flex items-start gap-3">
                  <InformationCircleIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-1">What happens next?</h4>
                    <ul className="text-blue-700 dark:text-blue-300 space-y-1">
                      <li>• Our team will review your request within 24-48 hours</li>
                      <li>• We'll check for mutual fit and interest</li>
                      <li>• If approved, we'll facilitate a direct introduction</li>
                      <li>• You'll receive an email with next steps</li>
                    </ul>
                  </div>
                </div>
              </div>

              <button
                onClick={handleClose}
                className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white font-medium 
                         rounded-lg transition-colors focus:outline-none focus:ring-2 
                         focus:ring-purple-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
              >
                Close
              </button>
            </div>
          ) : (
            /* Form State */
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* User Info Display */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <UserIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">
                      {getUserDisplayName()}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {user?.email}
                    </div>
                  </div>
                </div>
              </div>



              {/* Message Input */}
              <div>
                <label htmlFor="userMessage" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Tell us about your interest in this opportunity
                </label>
                <textarea
                  id="userMessage"
                  value={userMessage}
                  onChange={handleMessageChange}
                  placeholder="Share your background, what excites you about this opportunity, and why you'd be a great fit..."
                  rows={6}
                  className={`w-full px-4 py-3 border rounded-lg resize-none focus:outline-none focus:ring-2 
                             transition-colors dark:bg-gray-700 dark:text-white ${
                    isOverLimit
                      ? 'border-red-300 focus:ring-red-500 focus:border-red-500 dark:border-red-600'
                      : 'border-gray-300 focus:ring-purple-500 focus:border-purple-500 dark:border-gray-600'
                  }`}
                  disabled={isSubmitting}
                />
                
                {/* Character Count */}
                <div className="flex justify-between items-center mt-2">
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    This message will be shared with the founder
                  </div>
                  <div className={`text-sm ${
                    isOverLimit 
                      ? 'text-red-600 dark:text-red-400' 
                      : isNearLimit 
                        ? 'text-yellow-600 dark:text-yellow-400'
                        : 'text-gray-500 dark:text-gray-400'
                  }`}>
                    {characterCount}/{MESSAGE_LIMIT}
                  </div>
                </div>
              </div>

              {/* Error Message */}
              {submitStatus === 'error' && errorMessage && (
                <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 
                               rounded-lg border border-red-200 dark:border-red-700">
                  <ExclamationTriangleIcon className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <span className="text-red-800 dark:text-red-300 text-sm">
                    {errorMessage}
                  </span>
                </div>
              )}

              {/* Info Box */}
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-700">
                <div className="flex items-start gap-3">
                  <InformationCircleIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800 dark:text-blue-200">
                    <p className="font-medium mb-1">How it works:</p>
                    <p>
                      Our team reviews all connection requests to ensure quality matches. 
                      If approved, we'll facilitate a direct introduction between you and the founder.
                    </p>
                  </div>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex items-center justify-between pt-4">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 
                           dark:hover:text-gray-200 font-medium transition-colors"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                
                <button
                  type="submit"
                  disabled={isSubmitting || !userMessage.trim() || isOverLimit}
                  className={`px-6 py-2 rounded-lg font-medium transition-all duration-200 
                             flex items-center gap-2 ${
                    isSubmitting || !userMessage.trim() || isOverLimit
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-purple-600 hover:bg-purple-700 text-white hover:scale-105 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800'
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <>
                      <PaperAirplaneIcon className="w-4 h-4" />
                      <span>Submit Request</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConnectionRequestModal;